import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Scheduler from './Scheduler';

const PARTICLE_COUNT = 12;

const createParticleConfigs = () =>
  Array.from({ length: PARTICLE_COUNT }, (_, index) => {
    const randomOpacity = 0.3 + Math.random() * 0.5;
    return {
      offsetX: Math.random() * 100 - 50,
      offsetY: Math.random() * 100 - 50,
      duration: 4 + Math.random() * 3,
      delay: index * 0.3,
      background: `rgba(0, 0, 0, ${randomOpacity})`,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`
    };
  });

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [contactMethod, setContactMethod] = useState('message'); // 'message' or 'meeting'
  const { t } = useTranslation('home');

  const serviceOptionKeys = [
    'contact.serviceOptions.customSoftware',
    'contact.serviceOptions.webDevelopment',
    'contact.serviceOptions.mobileApp',
    'contact.serviceOptions.uiux',
    'contact.serviceOptions.cloud',
    'contact.serviceOptions.ai',
    'contact.serviceOptions.other',
  ];

  const leftParticleConfigs = useMemo(() => createParticleConfigs(), []);
  const rightParticleConfigs = useMemo(() => createParticleConfigs(), []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    /* TESTING MODE: Comment out formspree and just show success
    console.log('📧 Form submitted (test mode):', formData);
    setSubmitted(true);

    // Reset form after 5 seconds for testing
    setTimeout(() => {
      setFormData({ name: '', email: '', company: '', service: '', message: '' });
    }, 5000);
*/
    // PRODUCTION MODE: Uncomment below to use formspree

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('company', formData.company);
    formDataToSend.append('service', formData.service);
    formDataToSend.append('message', formData.message);

    fetch('https://formspree.io/f/myzlgorb', {
      method: 'POST',
      body: formDataToSend,
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(response => {
        if (response.ok) {
          console.log('✅ Email sent successfully');
          setSubmitted(true);
          setTimeout(() => {
            setFormData({ name: '', email: '', company: '', service: '', message: '' });
            setSubmitted(false);
          }, 3000);
        } else {
          console.error('❌ Failed to send email');
          setError('Failed to send message. Please try again.');
        }
      })
      .catch((error) => {
        console.error('❌ Error sending email:', error);
        setError('Failed to send message. Please try again.');
      });
  };

  return (
    <section className="bg-white min-h-screen py-8 relative mb-20 flex items-center" id="contact" data-section="contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 relative"
        >

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block relative"
          >
            {/* Badge */}
            <p className="text-secondary-accent text-sm font-semibold mb-2 uppercase tracking-wider flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-secondary-accent rounded-full"></span>
              {t('contact.badge')}
            </p>
          </motion.div>

          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-text-primary mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-base sm:text-lg text-grey-accent max-w-2xl mx-auto">
            {t('contact.description')}
          </p>
        </motion.div>

        {/* Contact Form Card with Decorative Elements */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="max-w-5xl mx-auto"
        >
          {/* Method Selection Tabs */}
          <div className="flex justify-center mb-8 relative z-20">
            <div className="bg-gray-100/50 p-1.5 rounded-xl inline-flex relative backdrop-blur-sm border border-gray-200">
              {/* Animated background for active tab */}
              <motion.div
                className="absolute inset-y-1.5 bg-white rounded-lg shadow-sm"
                initial={false}
                animate={{
                  left: contactMethod === 'message' ? '6px' : '50%',
                  right: contactMethod === 'message' ? '50%' : '6px',
                  // Alternatively calculate explicit widths if needed, but percentages work for even split
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                style={{ width: 'calc(50% - 9px)' }} // precise calculation for gap
              />

              <button
                onClick={() => setContactMethod('message')}
                className={`relative px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 min-w-[140px] z-10 flex items-center justify-center gap-2 ${contactMethod === 'message' ? 'text-text-primary' : 'text-grey-accent hover:text-text-primary'
                  }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                {t('contact.method.message')}
              </button>
              <button
                onClick={() => setContactMethod('meeting')}
                className={`relative px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-200 min-w-[140px] z-10 flex items-center justify-center gap-2 ${contactMethod === 'meeting' ? 'text-text-primary' : 'text-grey-accent hover:text-text-primary'
                  }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                {t('contact.method.meeting')}
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {contactMethod === 'message' ? (
              <motion.div
                key="message"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                {/* Decorative Left Element - Animated Particles */}
                <div className="absolute -left-40 top-1/4 hidden lg:block w-56 h-56 pointer-events-none">
                  {/* Particle dots */}
                  {leftParticleConfigs.map((config, i) => (
                    <motion.div
                      key={i}
                      animate={{
                        x: [0, config.offsetX, 0],
                        y: [0, config.offsetY, 0],
                        opacity: [0, 0.8, 0],
                      }}
                      transition={{
                        duration: config.duration,
                        repeat: Infinity,
                        delay: config.delay,
                        ease: 'easeInOut'
                      }}
                      className="absolute w-2 h-2 rounded-full"
                      style={{
                        background: config.background,
                        left: config.left,
                        top: config.top,
                      }}
                    ></motion.div>
                  ))}
                </div>

                {/* Decorative Right Element - Animated Particles */}
                <div className="absolute -right-40 top-1/3 hidden lg:block w-56 h-56 pointer-events-none">
                  {/* Particle dots */}
                  {rightParticleConfigs.map((config, i) => (
                    <motion.div
                      key={i}
                      animate={{
                        x: [0, config.offsetX, 0],
                        y: [0, config.offsetY, 0],
                        opacity: [0, 0.8, 0],
                      }}
                      transition={{
                        duration: config.duration,
                        repeat: Infinity,
                        delay: config.delay,
                        ease: 'easeInOut'
                      }}
                      className="absolute w-2 h-2 rounded-full"
                      style={{
                        background: config.background,
                        left: config.left,
                        top: config.top,
                      }}
                    ></motion.div>
                  ))}
                </div>

                {/* Main Card */}
                <div className="bg-gradient-to-br from-[#F9F9F9] to-[#FAFAFA] rounded-3xl p-10 border border-[#EDEDED] shadow-lg hover:shadow-xl transition-all duration-300 relative z-10">
                  {submitted ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex flex-col items-center justify-center py-20 px-4"
                    >
                      {/* Success Icon with Circle Animation */}
                      <div className="relative mb-8">
                        {/* Expanding circle background */}
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.1, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                          className="absolute inset-0 bg-secondary-accent/10 rounded-full"
                          style={{ width: '96px', height: '96px', left: '-12px', top: '-12px' }}
                        />

                        {/* Main icon circle */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2, duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
                          className="relative w-[72px] h-[72px] bg-secondary-accent rounded-full flex items-center justify-center"
                        >
                          {/* Checkmark with draw animation */}
                          <motion.svg
                            className="w-10 h-10 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                          >
                            <motion.path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M5 13l4 4L19 7"
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{ delay: 0.5, duration: 0.4, ease: "easeOut" }}
                            />
                          </motion.svg>
                        </motion.div>
                      </div>

                      {/* Title */}
                      <motion.h3
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.4 }}
                        className="text-3xl font-bold text-text-primary mb-3 text-center"
                      >
                        {t('contact.success.title')}
                      </motion.h3>

                      {/* Description */}
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.4 }}
                        className="text-base text-grey-accent text-center max-w-md mb-8"
                      >
                        {t('contact.success.description')}
                      </motion.p>

                      {/* Call-to-Action Buttons */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.4 }}
                        className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto"
                      >
                        <a
                          href="#cases"
                          className="group px-6 py-3 bg-text-primary text-white rounded-xl font-semibold hover:bg-secondary-accent transition-all duration-300 text-center flex items-center justify-center gap-2"
                        >
                          <span>{t('contact.success.viewWork')}</span>
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </a>
                        <button
                          onClick={() => setSubmitted(false)}
                          className="px-6 py-3 border-2 border-grey-accent/30 text-grey-accent rounded-xl font-semibold hover:border-text-primary hover:text-text-primary transition-all duration-300"
                        >
                          {t('contact.success.sendAnother')}
                        </button>
                      </motion.div>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                          {error}
                        </div>
                      )}
                      {/* Two Column Layout for Name and Email */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="name" className="block text-sm font-semibold text-text-primary mb-2">
                            {t('contact.form.name')} *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white rounded-xl border-2 border-[#EDEDED] text-text-primary placeholder-grey-accent focus:border-secondary-accent focus:outline-none transition-all duration-200 hover:border-[#E0E0E0]"
                            placeholder={t('contact.form.namePlaceholder')}
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-semibold text-text-primary mb-2">
                            {t('contact.form.email')} *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-white rounded-xl border-2 border-[#EDEDED] text-text-primary placeholder-grey-accent focus:border-secondary-accent focus:outline-none transition-all duration-200 hover:border-[#E0E0E0]"
                            placeholder={t('contact.form.emailPlaceholder')}
                          />
                        </div>
                      </div>

                      {/* Two Column Layout for Company and Service */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="company" className="block text-sm font-semibold text-text-primary mb-2">
                            {t('contact.form.company')}
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white rounded-xl border-2 border-[#EDEDED] text-text-primary placeholder-grey-accent focus:border-secondary-accent focus:outline-none transition-all duration-200 hover:border-[#E0E0E0]"
                            placeholder={t('contact.form.companyPlaceholder')}
                          />
                        </div>

                        <div>
                          <label htmlFor="service" className="block text-sm font-semibold text-text-primary mb-2">
                            {t('contact.form.service')}
                          </label>
                          <select
                            id="service"
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                            className="w-full px-4 py-3 bg-white rounded-xl border-2 border-[#EDEDED] text-text-primary focus:border-secondary-accent focus:outline-none transition-all duration-200 hover:border-[#E0E0E0] appearance-none cursor-pointer"
                          >
                            <option value="">{t('contact.form.servicePlaceholder')}</option>
                            {serviceOptionKeys.map((key, index) => (
                              <option key={index} value={t(key)}>
                                {t(key)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Message Field */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-text-primary mb-2">
                          {t('contact.form.message')} *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows="4"
                          className="w-full px-4 py-3 bg-white rounded-xl border-2 border-[#EDEDED] text-text-primary placeholder-grey-accent focus:border-secondary-accent focus:outline-none transition-all duration-200 hover:border-[#E0E0E0] resize-none"
                          placeholder={t('contact.form.messagePlaceholder')}
                        />
                      </div>

                      {/* Submit Button */}
                      <motion.button
                        type="submit"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full px-8 py-3 bg-text-primary text-white rounded-xl font-semibold hover:bg-secondary-accent transition-all duration-300 flex items-center justify-center gap-2 group"
                      >
                        <span>{t('contact.form.submit')}</span>
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </motion.button>

                      {/* Helper Text */}
                      <p className="text-xs text-grey-accent text-center">
                        {t('contact.form.helper')}
                      </p>
                    </form>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="meeting"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="relative z-10"
              >
                <Scheduler />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>


      </div>
    </section>
  );
};

export default Contact;
